import * as THREE from "three";
import { canMoveToFoundation, canMoveToTableau, canPickUpRun, foundationIndex } from "../game/rules";
import { FOUNDATION_SUITS, cardId, type Card, type GameState, type MoveSource } from "../game/types";
import { buildCardGeometry } from "./cardGeometry";
import { getCardBackTexture, getCardFaceTexture, drawSuitSymbol } from "./cardTextures";
import type { TavernScene } from "./scene";
import { easeOut } from "./tween";
import { makeCanvasWH } from "./textures";
import { FELT_DEPTH, FELT_WIDTH, FLOOR_Y, TABLE_CENTER_Z } from "./table";

/** Dimensions d'une carte en unites monde. */
const CARD_W = 0.8;
const CARD_H = 1.2;
const CARD_T = 0.016;
const CARD_CORNER_RADIUS = 0.055;
const CARD_GEOMETRY = buildCardGeometry(CARD_W, CARD_H, CARD_T, CARD_CORNER_RADIUS);

/** Disposition du tapis (cahier des charges §4) : rangee haute pioche/defausse
 * + fondations, puis les 7 colonnes du tableau qui descendent vers le joueur. */
const COL_SPACING = 0.98;
function colX(col: number): number {
  return -2.94 + col * COL_SPACING;
}
const TOP_Z = -2.35;
const STOCK_X = colX(0);
const WASTE_X = colX(1);
/** Ecart horizontal des cartes de l'eventail de defausse (tirage par 3). */
const WASTE_FAN_X = 0.3;
const TABLEAU_Z0 = -0.85;
/** Chevauchement vertical des cartes d'une colonne (face cachee / visible). */
const DZ_DOWN = 0.16;
const DZ_UP = 0.34;
/**
 * Fin de colonne maximale : au-dela, les chevauchements se compriment pour
 * rester sur la table. Volontairement resserre (les colonnes tres longues,
 * face visible sur toute leur hauteur, restent rares en pratique) : ca borne
 * l'emprise verticale du tapis de jeu, donc la table/camera peuvent se
 * rapprocher davantage sans jamais faire deborder une pile, meme extreme.
 */
const MAX_TABLEAU_LENGTH = 2.8;

/** Hauteur de saisie d'une carte pendant le glisser-deposer. */
const DRAG_LIFT = 0.55;

/** Duree d'appui immobile qui vaut double-clic/double-tap (resolution de destination). */
const LONG_PRESS_MS = 275;

// ------------------------------------------------------- ombre de deplacement

/**
 * Ombre de contact stylisee, en complement de la vraie shadow map (cf.
 * scene.ts) : la lumiere de table est quasi a la verticale d'une camera
 * elle-meme quasi verticale, donc l'ombre projetee reelle d'une carte
 * soulevee tombe presque exactement dessous — invisible depuis cette vue,
 * quelle que soit son intensite. Ce halo flotte juste sous la carte (et non
 * au niveau du feutre) : il reste donc toujours visible AU-DESSUS de
 * n'importe quelle pile survolee au lieu de disparaitre dessous, invisible
 * au repos, il se renforce et s'elargit a mesure que la carte s'eleve.
 */
const SHADOW_BELOW_GAP = 0.02;
const SHADOW_MAX_LIFT = 0.4;
const SHADOW_MAX_OPACITY = 0.7;
const SHADOW_EXTRA_SCALE = 0.45;
/** Leger decalage constant (repere carte, avant rotation Y) : lecture "ombre projetee" plutot qu'un halo purement symetrique. */
const SHADOW_OFFSET_X = 0.05;
const SHADOW_OFFSET_Z = 0.11;
const SHADOW_GEOMETRY = new THREE.PlaneGeometry(CARD_W * 1.5, CARD_H * 1.5);
let sharedShadowTexture: THREE.CanvasTexture | null = null;

/**
 * Silhouette de carte (rectangle arrondi) floutee au canvas : ombre projetee
 * credible plutot qu'un halo circulaire generique. Le canvas est carre mais
 * mappe sur un plan aux proportions de la carte (SHADOW_GEOMETRY) : le
 * rectangle dessine en repere carre s'etire donc a l'affichage pour suivre
 * exactement ce format, pas besoin de compenser l'aspect ratio ici.
 */
function getShadowTexture(): THREE.CanvasTexture {
  if (sharedShadowTexture) return sharedShadowTexture;
  const size = 256;
  const { canvas, ctx } = makeCanvasWH(size, size);
  const pad = size * 0.24;
  ctx.filter = "blur(16px)";
  ctx.fillStyle = "rgba(0,0,0,0.85)";
  ctx.beginPath();
  ctx.roundRect(pad, pad, size - pad * 2, size - pad * 2, size * 0.09);
  ctx.fill();
  ctx.filter = "none";
  sharedShadowTexture = new THREE.CanvasTexture(canvas);
  return sharedShadowTexture;
}

/** Angle pseudo-aleatoire (deterministe) pour eparpiller naturellement les cartes d'une pile. */
function scatterAngle(seed: number, maxRad: number): number {
  return (((seed * 0.6180339887498949) % 1) - 0.5) * 2 * maxRad;
}

interface Pose {
  position: THREE.Vector3;
  rotY: number;
  /** 0 = face visible, PI = dos visible. */
  flip: number;
}

interface CardMesh {
  group: THREE.Group;
  mesh: THREE.Object3D;
  frontMesh: THREE.Mesh;
  backMesh: THREE.Mesh;
  faceMat: THREE.MeshStandardMaterial;
  flip: number;
  card: Card;
  /** Ombre de contact stylisee, flotte juste sous la carte (pas un enfant de `group` : ne doit pas suivre sa rotation). */
  shadowMesh: THREE.Mesh;
  shadowMat: THREE.MeshBasicMaterial;
}

/** Cible de depot d'un glisser-deposer (ou d'un double-clic vers les fondations). */
export type DropTarget = { type: "foundation" } | { type: "tableau"; col: number };

interface ValidTarget {
  target: DropTarget;
  center: THREE.Vector3;
  /** Carte (ou emplacement vide) a surligner pendant le glisser. */
  topId: string | null;
  slotIndex: number | null;
}

interface CascadeCard {
  cm: CardMesh;
  velocity: THREE.Vector3;
  spin: number;
  delay: number;
  done: boolean;
}

/**
 * Vue 3D du tapis de Klondike : pioche, defausse en eventail, fondations et
 * 7 colonnes. La vue est pilotee par l'ETAT du moteur : apres chaque action,
 * le controleur appelle `sync(state)` et chaque carte dont la pose a change
 * glisse vers sa nouvelle place — deplacements, retours elastiques, undo et
 * auto-complete passent tous par le meme chemin. Les interactions (glisser-
 * deposer, double-clic, clic pioche) remontent au controleur par callbacks.
 */
export class SolitaireView {
  /** Clic/tap sur la pioche (ou son emplacement vide) : tirer / recycler. */
  onStockClick: (() => void) | null = null;
  /** Demande de coup (drop ou double-clic). Doit retourner true si le moteur l'a accepte. */
  onDrop: ((source: MoveSource, target: DropTarget) => boolean) | null = null;
  /** Double-clic/tap sur une carte de sommet : envoi automatique vers sa fondation, ou a defaut vers la premiere colonne du tableau qui l'accueille. */
  onDoubleTap: ((source: MoveSource) => boolean) | null = null;
  /** Une carte vient d'etre saisie (petit son de glissement). */
  onPickUp: (() => void) | null = null;
  /** Rebond d'une carte pendant la cascade de victoire. */
  onCascadeBounce: (() => void) | null = null;

  /** Surbrillance des cibles valides pendant le glisser (option, cf. reglages). */
  highlightEnabled = true;

  private cards = new Map<string, CardMesh>();
  private state: GameState | null = null;
  private interactive = false;

  private raycaster = new THREE.Raycaster();
  private pointer = new THREE.Vector2();
  private dragPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);

  /** Saisie en cours (avant ou pendant le glisser). */
  private grab: {
    source: MoveSource;
    run: CardMesh[];
    startScreen: THREE.Vector2;
    grabOffsets: THREE.Vector3[];
    dragging: boolean;
    targets: ValidTarget[];
    isStock: boolean;
  } | null = null;

  private lastTapId: string | null = null;
  private lastTapTime = 0;

  /** Appui long en cours (equivalent du double-clic) : declenche a MOUSEDOWN_HOLD_MS si le doigt/la souris ne bouge pas. */
  private longPressTimer: ReturnType<typeof setTimeout> | null = null;

  private slotMeshes: THREE.Mesh[] = [];
  private stockSlot: THREE.Mesh;
  private cascade: CascadeCard[] = [];

  constructor(private scene: TavernScene) {
    const el = scene.renderer.domElement;
    el.addEventListener("pointerdown", (e) => this.handlePointerDown(e));
    el.addEventListener("pointermove", (e) => this.handlePointerMove(e));
    el.addEventListener("pointerup", (e) => this.handlePointerUp(e));
    el.addEventListener("pointercancel", () => this.cancelGrab());
    scene.onFrame((dt) => this.updateCascade(dt));

    this.stockSlot = this.buildSlots();
  }

  // ------------------------------------------------------------- distribution

  /**
   * Nouvelle donne : toutes les cartes partent empilees sur l'emplacement de
   * la pioche, puis les 28 cartes du tableau se posent en cascade rapide,
   * colonne par colonne (cahier des charges §7). `onCardDealt` est appele au
   * depart de chaque carte (petit son de glissement).
   */
  async deal(state: GameState, onCardDealt?: () => void): Promise<void> {
    this.clearAll();
    this.state = state;

    // Ordre de vol : colonne par colonne, dans l'ordre de distribution.
    const dealOrder: Card[] = [];
    for (const pile of state.tableau) dealOrder.push(...pile);

    // Pile de depart : la pioche en dessous, puis les cartes du tableau, la
    // PREMIERE a voler tout en haut.
    const spawn: Card[] = [...state.stock, ...[...dealOrder].reverse()];
    spawn.forEach((card, level) => {
      const cm = this.makeCard(card);
      cm.flip = Math.PI;
      cm.group.position.set(STOCK_X, CARD_T / 2 + level * CARD_T, TOP_Z);
      cm.group.rotation.y = scatterAngle(level, 0.05);
      this.syncMesh(cm);
    });

    const poses = this.computePoses(state);
    const anims: Promise<void>[] = [];
    dealOrder.forEach((card, i) => {
      const cm = this.cards.get(cardId(card))!;
      const pose = poses.get(cardId(card))!;
      anims.push(
        this.scene.tweens.wait(0.05 + i * 0.05).then(() => {
          onCardDealt?.();
          return this.tweenToPose(cm, pose, 0.3);
        }),
      );
    });
    // La pioche se tasse a sa place immediatement (elle est deja au bon endroit,
    // au niveau pres : les cartes du dessus sont parties).
    for (const card of state.stock) {
      const cm = this.cards.get(cardId(card))!;
      anims.push(this.tweenToPose(cm, poses.get(cardId(card))!, 0.3));
    }
    await Promise.all(anims);
    this.updateSlots(state);
    this.interactive = true;
  }

  /** Aligne la vue sur un nouvel etat du moteur : chaque carte qui a change de pose glisse vers sa place. */
  async sync(state: GameState, options: { duration?: number } = {}): Promise<void> {
    this.state = state;
    this.updateSlots(state);
    const duration = options.duration ?? 0.28;
    const poses = this.computePoses(state);
    const anims: Promise<void>[] = [];
    for (const [id, pose] of poses) {
      const cm = this.cards.get(id);
      if (!cm) continue;
      if (!this.poseMatches(cm, pose)) anims.push(this.tweenToPose(cm, pose, duration));
    }
    await Promise.all(anims);
  }

  setInteractive(value: boolean): void {
    this.interactive = value;
    if (!value) this.cancelGrab();
  }

  // ------------------------------------------------------------- indice

  /** Halo dore bref sur les cartes concernees par l'indice (source et cible). */
  flashHint(ids: string[]): void {
    for (const id of ids) {
      const cm = this.cards.get(id);
      if (!cm) continue;
      void this.scene.tweens.run(
        1.3,
        (t) => {
          const pulse = Math.max(0, Math.sin(t * Math.PI * 3));
          cm.faceMat.emissive.set("#ffbf60");
          cm.faceMat.emissiveIntensity = 0.35 + pulse * 0.5;
        },
        (t) => t,
        `hint-${id}`,
      ).then(() => this.resetGlow(cm));
    }
  }

  // ------------------------------------------------------------- victoire

  /**
   * Cascade finale classique du genre : les cartes des fondations debordent
   * une a une et rebondissent sur la table (puis jusqu'au sol), pendant que
   * l'ecran "GAGNE" s'affiche en surimpression (cahier des charges §7).
   */
  startVictoryCascade(): void {
    if (!this.state) return;
    this.interactive = false;
    let order = 0;
    for (let rank = 12; rank >= 0; rank--) {
      for (let f = 0; f < 4; f++) {
        const card = this.state.foundations[f]?.[rank];
        if (!card) continue;
        const cm = this.cards.get(cardId(card));
        if (!cm) continue;
        const side = cm.group.position.x >= 0 ? 1 : -1;
        this.cascade.push({
          cm,
          velocity: new THREE.Vector3(
            side * (0.6 + Math.random() * 1.8),
            1.2 + Math.random() * 1.4,
            1.0 + Math.random() * 2.0,
          ),
          spin: (Math.random() - 0.5) * 6,
          delay: order * 0.14,
          done: false,
        });
        order++;
      }
    }
  }

  private updateCascade(dt: number): void {
    if (this.cascade.length === 0) return;
    const gravity = 7.5;
    for (const item of this.cascade) {
      if (item.done) continue;
      if (item.delay > 0) {
        item.delay -= dt;
        continue;
      }
      const pos = item.cm.group.position;
      item.velocity.y -= gravity * dt;
      pos.addScaledVector(item.velocity, dt);
      item.cm.group.rotation.y += item.spin * dt;

      // Rebond sur la table tant qu'on est au-dessus, sinon sur le sol.
      const onTable =
        Math.abs(pos.x) < FELT_WIDTH / 2 - 0.2 && Math.abs(pos.z - TABLE_CENTER_Z) < FELT_DEPTH / 2 - 0.2;
      const ground = onTable ? CARD_T / 2 : FLOOR_Y + 0.05;
      if (pos.y < ground && item.velocity.y < 0) {
        pos.y = ground;
        item.velocity.y *= -0.68;
        item.velocity.x *= 0.96;
        item.velocity.z *= 0.96;
        if (Math.abs(item.velocity.y) > 0.6) this.onCascadeBounce?.();
        if (!onTable && Math.abs(item.velocity.y) < 0.4) item.done = true;
      }
      if (Math.abs(pos.x) > 12 || Math.abs(pos.z) > 12) item.done = true;
    }
  }

  // ------------------------------------------------------------- poses

  private computePoses(state: GameState): Map<string, Pose> {
    const poses = new Map<string, Pose>();

    state.stock.forEach((card, i) => {
      poses.set(cardId(card), {
        position: new THREE.Vector3(STOCK_X, CARD_T / 2 + i * CARD_T, TOP_Z),
        rotY: scatterAngle(i, 0.05),
        flip: Math.PI,
      });
    });

    const shown = state.drawCount === 3 ? 3 : 1;
    const base = Math.max(0, state.waste.length - shown);
    state.waste.forEach((card, i) => {
      const fan = Math.max(0, i - base);
      poses.set(cardId(card), {
        position: new THREE.Vector3(WASTE_X + fan * WASTE_FAN_X, CARD_T / 2 + i * CARD_T, TOP_Z),
        rotY: scatterAngle(i, 0.03),
        flip: 0,
      });
    });

    state.foundations.forEach((pile, f) => {
      const x = colX(3 + f);
      pile.forEach((card, i) => {
        poses.set(cardId(card), {
          position: new THREE.Vector3(x, CARD_T / 2 + i * CARD_T, TOP_Z),
          rotY: scatterAngle(i + f * 5, 0.03),
          flip: 0,
        });
      });
    });

    state.tableau.forEach((pile, col) => {
      const offsets = this.tableauOffsets(pile);
      pile.forEach((card, i) => {
        poses.set(cardId(card), {
          position: new THREE.Vector3(colX(col), CARD_T / 2 + i * CARD_T, TABLEAU_Z0 + offsets[i]!),
          rotY: 0,
          flip: card.faceUp ? 0 : Math.PI,
        });
      });
    });

    return poses;
  }

  /** Decalages en z des cartes d'une colonne, comprimes si la pile deborderait de la table. */
  private tableauOffsets(pile: readonly Card[]): number[] {
    const offsets: number[] = [];
    let z = 0;
    for (let i = 0; i < pile.length; i++) {
      offsets.push(z);
      z += pile[i]!.faceUp ? DZ_UP : DZ_DOWN;
    }
    const last = offsets[offsets.length - 1] ?? 0;
    if (last > MAX_TABLEAU_LENGTH) {
      const scale = MAX_TABLEAU_LENGTH / last;
      for (let i = 0; i < offsets.length; i++) offsets[i]! *= scale;
    }
    return offsets;
  }

  private poseMatches(cm: CardMesh, pose: Pose): boolean {
    return (
      cm.group.position.distanceToSquared(pose.position) < 1e-6 &&
      Math.abs(cm.group.rotation.y - pose.rotY) < 1e-3 &&
      Math.abs(cm.flip - pose.flip) < 1e-3
    );
  }

  private tweenToPose(cm: CardMesh, pose: Pose, duration: number): Promise<void> {
    const fromPos = cm.group.position.clone();
    const fromRotY = cm.group.rotation.y;
    const fromFlip = cm.flip;
    const horizontal = Math.hypot(pose.position.x - fromPos.x, pose.position.z - fromPos.z);
    // Petit arc vertical pendant le vol (jamais une glissade a plat), plus
    // marque quand la carte se retourne (pincee entre deux doigts).
    const flipDelta = Math.abs(pose.flip - fromFlip);
    const lift = flipDelta > 0.1 ? 0.3 : horizontal > 0.4 ? 0.22 : 0;
    return this.scene.tweens.run(
      duration,
      (t) => {
        cm.group.position.lerpVectors(fromPos, pose.position, t);
        const envelope = Math.sin(t * Math.PI);
        cm.group.position.y += envelope * lift;
        cm.group.rotation.y = fromRotY + (pose.rotY - fromRotY) * t;
        cm.flip = fromFlip + (pose.flip - fromFlip) * t;
        // L'ombre suit l'enveloppe du vol (0 -> pleine force au sommet -> 0),
        // pas l'amplitude reelle de l'arc : un arc court (lift=0.22) doit
        // rester aussi visible qu'un grand, sinon l'ombre est trop discrete
        // sur la plupart des coups (appui long/double-clic vers une colonne
        // proche), qui ne soulevent la carte que tres peu visuellement.
        this.syncMesh(cm, lift > 0 ? envelope * SHADOW_MAX_LIFT : 0);
      },
      easeOut,
      transformKey(cm),
    );
  }

  // ------------------------------------------------------------- interactions

  private handlePointerDown(event: PointerEvent): void {
    if (!this.interactive || !this.state || this.grab) return;
    const hit = this.pick(event);
    if (!hit) return;
    this.scene.renderer.domElement.setPointerCapture(event.pointerId);

    if (hit.zone === "stock") {
      this.grab = {
        source: { type: "waste" }, // inutilise pour la pioche
        run: [],
        startScreen: new THREE.Vector2(event.clientX, event.clientY),
        grabOffsets: [],
        dragging: false,
        targets: [],
        isStock: true,
      };
      return;
    }

    const source = hit.source!;
    const run = this.runFor(source);
    if (run.length === 0) return;
    const planePoint = this.planePoint(event);
    this.grab = {
      source,
      run,
      startScreen: new THREE.Vector2(event.clientX, event.clientY),
      grabOffsets: run.map((cm) => cm.group.position.clone().sub(planePoint ?? cm.group.position)),
      dragging: false,
      targets: this.computeValidTargets(source, run),
      isStock: false,
    };
    this.setPressScale(run, true);

    // Appui long immobile = double-clic : resout la destination sans attendre
    // un second tap. Annule si le doigt/la souris bouge (glisser) ou est relache avant.
    this.longPressTimer = setTimeout(() => {
      this.longPressTimer = null;
      if (!this.grab || this.grab.dragging || this.grab.isStock) return;
      const { source: grabSource, run: grabRun } = this.grab;
      this.grab = null;
      this.lastTapId = null;
      this.setPressScale(grabRun, false);
      const ok = this.onDoubleTap?.(grabSource) ?? false;
      if (!ok) this.shakeInvalid(grabRun);
    }, LONG_PRESS_MS);
  }

  private clearLongPressTimer(): void {
    if (this.longPressTimer === null) return;
    clearTimeout(this.longPressTimer);
    this.longPressTimer = null;
  }

  /** Leger grossissement au mousedown/touch (retour tactile), retire au relachement. */
  private setPressScale(cards: CardMesh[], pressed: boolean): void {
    const to = pressed ? 1.08 : 1;
    for (const cm of cards) {
      const from = cm.group.scale.x;
      void this.scene.tweens.run(
        0.1,
        (t) => cm.group.scale.setScalar(from + (to - from) * t),
        easeOut,
        scaleKey(cm),
      );
    }
  }

  /** Secoue une carte (appui long ou double-tap sans destination possible). */
  private shakeInvalid(cards: CardMesh[]): void {
    for (const cm of cards) {
      const baseX = cm.group.position.x;
      void this.scene.tweens.run(
        0.32,
        (t) => {
          cm.group.position.x = baseX + Math.sin(t * Math.PI * 7) * 0.07 * (1 - t);
          this.syncMesh(cm);
        },
        (t) => t,
        transformKey(cm),
      );
    }
  }

  private handlePointerMove(event: PointerEvent): void {
    if (!this.grab || this.grab.isStock) {
      this.updateCursor(event);
      return;
    }
    const grab = this.grab;
    const dist = Math.hypot(event.clientX - grab.startScreen.x, event.clientY - grab.startScreen.y);
    if (!grab.dragging && dist > 7) {
      grab.dragging = true;
      this.clearLongPressTimer();
      this.onPickUp?.();
      for (const cm of grab.run) this.scene.tweens.cancel(transformKey(cm));
      if (this.highlightEnabled) this.highlightTargets(grab.targets, true);
      this.scene.renderer.domElement.style.cursor = "grabbing";
    }
    if (!grab.dragging) return;
    const point = this.planePoint(event);
    if (!point) return;
    grab.run.forEach((cm, i) => {
      const offset = grab.grabOffsets[i]!;
      const liftHeight = DRAG_LIFT + i * 0.06;
      cm.group.position.set(point.x + offset.x, liftHeight, point.z + offset.z);
      this.syncMesh(cm, liftHeight);
    });
    // Le glisser-deposer deplace les cartes hors du systeme de tweens (position
    // ecrite directement ci-dessus) : sans ce forcage, la shadow map — mise a
    // jour uniquement quand un tween tourne (cf. scene.ts, cout de perf) —
    // resterait figee et l'ombre ne suivrait pas la carte soulevee.
    this.scene.renderer.shadowMap.needsUpdate = true;
  }

  private handlePointerUp(event: PointerEvent): void {
    this.clearLongPressTimer();
    const grab = this.grab;
    if (!grab || !this.state) {
      this.grab = null;
      return;
    }
    this.grab = null;
    this.setPressScale(grab.run, false);

    if (grab.isStock) {
      this.onStockClick?.();
      return;
    }

    if (!grab.dragging) {
      // Simple tap : detection du double-clic/double-tap (resolution de destination).
      const first = grab.run[0];
      if (first) {
        const id = cardId(first.card);
        const now = performance.now();
        if (this.lastTapId === id && now - this.lastTapTime < 420) {
          this.lastTapId = null;
          const ok = this.onDoubleTap?.(grab.source) ?? false;
          if (!ok) this.shakeInvalid(grab.run);
        } else {
          this.lastTapId = id;
          this.lastTapTime = now;
        }
      }
      return;
    }

    this.highlightTargets(grab.targets, false);
    this.scene.renderer.domElement.style.cursor = "default";

    const point = this.planePoint(event);
    let accepted = false;
    if (point) {
      let best: ValidTarget | null = null;
      let bestDist = 1.15;
      for (const target of grab.targets) {
        const d = Math.hypot(point.x - target.center.x, point.z - target.center.z);
        if (d < bestDist) {
          bestDist = d;
          best = target;
        }
      }
      if (best) accepted = this.onDrop?.(grab.source, best.target) ?? false;
    }
    if (!accepted) {
      // Retour elastique : la vue re-glisse vers l'etat inchange du moteur.
      void this.sync(this.state, { duration: 0.24 });
    }
  }

  private cancelGrab(): void {
    this.clearLongPressTimer();
    if (!this.grab) return;
    const wasDragging = this.grab.dragging;
    this.highlightTargets(this.grab.targets, false);
    this.setPressScale(this.grab.run, false);
    this.grab = null;
    if (wasDragging && this.state) void this.sync(this.state, { duration: 0.24 });
  }

  /** Cartes a saisir pour une source donnee (une seule, ou la sequence du tableau). */
  private runFor(source: MoveSource): CardMesh[] {
    const state = this.state!;
    if (source.type === "waste") {
      const top = state.waste[state.waste.length - 1];
      return top ? [this.cards.get(cardId(top))!] : [];
    }
    if (source.type === "foundation") {
      const pile = state.foundations[foundationIndex(source.suit)]!;
      const top = pile[pile.length - 1];
      return top ? [this.cards.get(cardId(top))!] : [];
    }
    const pile = state.tableau[source.col]!;
    if (!canPickUpRun(pile, source.index)) return [];
    return pile.slice(source.index).map((card) => this.cards.get(cardId(card))!);
  }

  /** Cibles legales pour la carte (ou sequence) saisie, avec leur centre de depot. */
  private computeValidTargets(source: MoveSource, run: CardMesh[]): ValidTarget[] {
    const state = this.state!;
    const targets: ValidTarget[] = [];
    const moving = run[0]!.card;

    // Fondation : uniquement pour une carte seule (jamais une sequence).
    if (run.length === 1 && source.type !== "foundation") {
      const f = foundationIndex(moving.suit);
      if (canMoveToFoundation(moving, state.foundations[f]!, moving.suit)) {
        const pile = state.foundations[f]!;
        const top = pile[pile.length - 1];
        targets.push({
          target: { type: "foundation" },
          center: new THREE.Vector3(colX(3 + f), 0, TOP_Z),
          topId: top ? cardId(top) : null,
          slotIndex: top ? null : 1 + f,
        });
      }
    }

    for (let col = 0; col < state.tableau.length; col++) {
      if (source.type === "tableau" && source.col === col) continue;
      const pile = state.tableau[col]!;
      if (!canMoveToTableau(moving, pile)) continue;
      const top = pile[pile.length - 1];
      const offsets = this.tableauOffsets(pile);
      const z = pile.length === 0 ? TABLEAU_Z0 + 0.3 : TABLEAU_Z0 + offsets[offsets.length - 1]! + DZ_UP;
      targets.push({
        target: { type: "tableau", col },
        center: new THREE.Vector3(colX(col), 0, z),
        topId: top ? cardId(top) : null,
        slotIndex: top ? null : 5 + col,
      });
    }
    return targets;
  }

  private highlightTargets(targets: ValidTarget[], on: boolean): void {
    if (!this.highlightEnabled && on) return;
    for (const target of targets) {
      if (target.topId) {
        const cm = this.cards.get(target.topId);
        if (!cm) continue;
        if (on) {
          cm.faceMat.emissive.set("#ffbf60");
          cm.faceMat.emissiveIntensity = 0.55;
        } else {
          this.resetGlow(cm);
        }
      } else if (target.slotIndex !== null) {
        const slot = this.slotMeshes[target.slotIndex];
        if (slot) (slot.material as THREE.MeshBasicMaterial).opacity = on ? 0.85 : 0.35;
      }
    }
  }

  // ------------------------------------------------------------- picking

  private pick(event: PointerEvent): { zone: "stock" | "card"; source?: MoveSource } | null {
    const state = this.state!;
    this.setPointerFromEvent(event);
    this.raycaster.setFromCamera(this.pointer, this.scene.camera);

    const pickables: THREE.Object3D[] = [this.stockSlot];
    for (const cm of this.cards.values()) pickables.push(cm.frontMesh, cm.backMesh);
    const hits = this.raycaster.intersectObjects(pickables, false);
    const first = hits[0];
    if (!first) return null;

    if (first.object === this.stockSlot) {
      return state.stock.length > 0 || state.waste.length > 0 ? { zone: "stock" } : null;
    }

    const cm = [...this.cards.values()].find((c) => c.frontMesh === first.object || c.backMesh === first.object);
    if (!cm) return null;
    const id = cardId(cm.card);

    // Ou est cette carte dans l'etat courant ?
    if (state.stock.some((card) => cardId(card) === id)) return { zone: "stock" };

    const wasteIndex = state.waste.findIndex((card) => cardId(card) === id);
    if (wasteIndex >= 0) {
      return wasteIndex === state.waste.length - 1 ? { zone: "card", source: { type: "waste" } } : null;
    }

    for (let f = 0; f < state.foundations.length; f++) {
      const pile = state.foundations[f]!;
      const idx = pile.findIndex((card) => cardId(card) === id);
      if (idx >= 0) {
        return idx === pile.length - 1
          ? { zone: "card", source: { type: "foundation", suit: FOUNDATION_SUITS[f]! } }
          : null;
      }
    }

    for (let col = 0; col < state.tableau.length; col++) {
      const pile = state.tableau[col]!;
      const idx = pile.findIndex((card) => cardId(card) === id);
      if (idx >= 0) {
        return pile[idx]!.faceUp ? { zone: "card", source: { type: "tableau", col, index: idx } } : null;
      }
    }
    return null;
  }

  private updateCursor(event: PointerEvent): void {
    if (!this.interactive || !this.state || this.grab) return;
    const hit = this.pick(event);
    this.scene.renderer.domElement.style.cursor = hit ? (hit.zone === "stock" ? "pointer" : "grab") : "default";
  }

  private setPointerFromEvent(event: PointerEvent): void {
    const rect = this.scene.renderer.domElement.getBoundingClientRect();
    this.pointer.set(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    );
  }

  private planePoint(event: PointerEvent): THREE.Vector3 | null {
    this.setPointerFromEvent(event);
    this.raycaster.setFromCamera(this.pointer, this.scene.camera);
    const point = new THREE.Vector3();
    return this.raycaster.ray.intersectPlane(this.dragPlane, point);
  }

  // ------------------------------------------------------------- interne

  private makeCard(card: Card): CardMesh {
    const faceTex = getCardFaceTexture(card);
    const backTex = getCardBackTexture();
    const sideMat = new THREE.MeshStandardMaterial({ color: "#cbb185", roughness: 0.9 });
    const faceMat = new THREE.MeshStandardMaterial({
      map: faceTex,
      roughness: 0.85,
      // Leger auto-eclairage permanent : les faces restent lisibles meme la ou
      // la lumiere des bougies ne porte pas (la carte est l'element n°1).
      emissive: "#8a7050",
      emissiveIntensity: 0.35,
      emissiveMap: faceTex,
    });
    const backMat = new THREE.MeshStandardMaterial({
      map: backTex,
      roughness: 0.85,
      emissive: "#6a5a40",
      emissiveIntensity: 0.3,
      emissiveMap: backTex,
    });

    const frontMesh = new THREE.Mesh(CARD_GEOMETRY.front, faceMat);
    const backMesh = new THREE.Mesh(CARD_GEOMETRY.back, backMat);
    const sideMesh = new THREE.Mesh(CARD_GEOMETRY.side, sideMat);
    for (const m of [frontMesh, backMesh, sideMesh]) {
      m.castShadow = true;
      m.receiveShadow = true;
    }

    const mesh = new THREE.Group();
    mesh.add(frontMesh, backMesh, sideMesh);
    const group = new THREE.Group();
    group.add(mesh);
    this.scene.scene.add(group);

    const shadowMat = new THREE.MeshBasicMaterial({
      map: getShadowTexture(),
      transparent: true,
      opacity: 0,
      depthWrite: false,
      toneMapped: false,
    });
    const shadowMesh = new THREE.Mesh(SHADOW_GEOMETRY, shadowMat);
    shadowMesh.rotation.x = -Math.PI / 2;
    this.scene.scene.add(shadowMesh);

    const cm: CardMesh = { group, mesh, frontMesh, backMesh, faceMat, flip: 0, card, shadowMesh, shadowMat };
    this.cards.set(cardId(card), cm);
    return cm;
  }

  /** Pose "carte a plat sur la table" : couchee (x = -PI/2) puis retournee selon `flip`. */
  /**
   * Pose "carte a plat sur la table" : couchee (x = -PI/2) puis retournee
   * selon `flip`. `lift` (elevation actuelle au-dessus de sa hauteur de
   * repos, cf. appelants) pilote l'ombre de contact : invisible au repos,
   * elle se renforce/s'elargit a mesure que la carte s'eleve. Positionnee
   * juste sous la carte elle-meme (et non au niveau du feutre) : elle reste
   * ainsi toujours au-dessus de toute pile survolee, jamais cachee dessous.
   */
  private syncMesh(cm: CardMesh, lift = 0): void {
    cm.mesh.rotation.set(-Math.PI / 2, cm.flip, 0);
    const liftT = THREE.MathUtils.clamp(lift / SHADOW_MAX_LIFT, 0, 1);
    cm.shadowMesh.position.set(
      cm.group.position.x + liftT * SHADOW_OFFSET_X,
      cm.group.position.y - SHADOW_BELOW_GAP,
      cm.group.position.z + liftT * SHADOW_OFFSET_Z,
    );
    cm.shadowMat.opacity = liftT * SHADOW_MAX_OPACITY;
    const scale = 1 + liftT * SHADOW_EXTRA_SCALE;
    cm.shadowMesh.scale.set(scale, scale, 1);
  }

  private resetGlow(cm: CardMesh): void {
    cm.faceMat.emissive.set("#8a7050");
    cm.faceMat.emissiveIntensity = 0.35;
  }

  /**
   * Marqueurs d'emplacements poses sur le feutre : pioche (fleche de
   * recyclage), fondations (enseigne en filigrane) et colonnes (simple
   * contour). Retourne le marqueur de pioche (cliquable pour recycler).
   * Index dans `slotMeshes` : 0 = pioche, 1-4 = fondations, 5-11 = colonnes.
   */
  private buildSlots(): THREE.Mesh {
    const makeSlot = (draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void): THREE.Mesh => {
      const { canvas, ctx } = makeCanvasWH(170, 256);
      ctx.strokeStyle = "rgba(232, 211, 160, 0.6)";
      ctx.lineWidth = 6;
      ctx.beginPath();
      ctx.roundRect(8, 8, 154, 240, 16);
      ctx.stroke();
      draw(ctx, 170, 256);
      const tex = new THREE.CanvasTexture(canvas);
      tex.colorSpace = THREE.SRGBColorSpace;
      const mesh = new THREE.Mesh(
        new THREE.PlaneGeometry(CARD_W * 1.06, CARD_H * 1.06),
        new THREE.MeshBasicMaterial({ map: tex, transparent: true, opacity: 0.35, depthWrite: false }),
      );
      mesh.rotation.x = -Math.PI / 2;
      this.scene.scene.add(mesh);
      this.slotMeshes.push(mesh);
      return mesh;
    };

    // 0 : pioche — fleche circulaire de recyclage.
    const stock = makeSlot((ctx, w, h) => {
      ctx.strokeStyle = "rgba(232, 211, 160, 0.7)";
      ctx.lineWidth = 9;
      ctx.beginPath();
      ctx.arc(w / 2, h / 2, 40, 0.4, Math.PI * 1.7);
      ctx.stroke();
      const tipAngle = 0.4;
      const tx = w / 2 + Math.cos(tipAngle) * 40;
      const ty = h / 2 + Math.sin(tipAngle) * 40;
      ctx.fillStyle = "rgba(232, 211, 160, 0.7)";
      ctx.beginPath();
      ctx.moveTo(tx + 14, ty - 2);
      ctx.lineTo(tx - 12, ty - 16);
      ctx.lineTo(tx - 2, ty + 16);
      ctx.closePath();
      ctx.fill();
    });
    stock.position.set(STOCK_X, 0.004, TOP_Z);

    // 1-4 : fondations — enseigne en filigrane.
    FOUNDATION_SUITS.forEach((suit, f) => {
      const slot = makeSlot((ctx, w, h) => {
        ctx.globalAlpha = 0.55;
        drawSuitSymbol(ctx, suit, w / 2, h / 2, 84, "#e8d3a0");
        ctx.globalAlpha = 1;
      });
      slot.position.set(colX(3 + f), 0.004, TOP_Z);
    });

    // 5-11 : colonnes du tableau — simple contour.
    for (let col = 0; col < 7; col++) {
      const slot = makeSlot(() => {});
      slot.position.set(colX(col), 0.004, TABLEAU_Z0 + 0.3);
    }

    return stock;
  }

  /** Les marqueurs ne se voient que sous un emplacement vide (sinon les cartes les couvrent). */
  private updateSlots(state: GameState): void {
    const setVisible = (index: number, visible: boolean): void => {
      const slot = this.slotMeshes[index];
      if (slot) {
        slot.visible = visible;
        (slot.material as THREE.MeshBasicMaterial).opacity = 0.35;
      }
    };
    setVisible(0, state.stock.length === 0);
    state.foundations.forEach((pile, f) => setVisible(1 + f, pile.length === 0));
    state.tableau.forEach((pile, col) => setVisible(5 + col, pile.length === 0));
  }

  private clearAll(): void {
    for (const cm of this.cards.values()) {
      this.scene.scene.remove(cm.group);
      this.scene.scene.remove(cm.shadowMesh);
    }
    this.cards.clear();
    this.cascade = [];
    this.grab = null;
    this.interactive = false;
  }
}

/** Cle stable du tween de transformation d'une carte : la plus recente annule la precedente. */
function transformKey(cm: CardMesh): string {
  return `card-transform-${cm.group.uuid}`;
}

/** Cle stable du tween d'echelle (independante de la position, cf. setPressScale). */
function scaleKey(cm: CardMesh): string {
  return `card-scale-${cm.group.uuid}`;
}
